import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb, Target, TrendingUp } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO, { ORG_JSON_LD } from "../components/SEO";

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <SEO
        title="About BuyerBeacon Pro — Built by 11x Founders for Small Business"
        description="BuyerBeacon Pro founders have started and operated 11 companies over two decades. Now they're sharing the intent-data platform that gives small businesses hot leads at a fraction of enterprise costs."
        path="/about"
        jsonLd={ORG_JSON_LD}
      />
      <Navbar />
      <main className="pt-28">
        {/* Hero */}
        <section className="relative overflow-hidden pb-12">
          <div className="pointer-events-none absolute left-1/2 top-[40px] -z-10 h-[360px] w-[680px] -translate-x-1/2 rounded-full bg-[#d4af37]/12 blur-[120px]" />
          <div className="mx-auto max-w-5xl px-5 lg:px-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              About
            </p>
            <h1 className="mt-3 max-w-3xl text-4xl font-medium tracking-tight text-[#1e1b4b] sm:text-5xl lg:text-6xl">
              Built by founders.{" "}
              <span className="text-slate-400">For founders.</span>
            </h1>
          </div>
        </section>

        {/* Story block */}
        <section className="mx-auto max-w-3xl px-5 pb-10 lg:px-8">
          <div className="text-[18px] leading-[1.75] text-slate-700">
            <p className="mb-6">
              <span className="float-left mr-3 mt-1 font-serif text-6xl font-bold leading-none text-[#1e1b4b]">
                B
              </span>
              uyerBeacon founders have started and operated{" "}
              <strong className="text-[#1e1b4b]">11 companies</strong> over the
              past two decades. They were frustrated with the battle to keep
              lead costs low and knew there had to be a better way for a small
              business to get truly hot leads at much lower costs.
            </p>
            <p className="mb-6">
              Now, by sharing access to the massive intent-data sourcing,
              qualifying, and cleaning effort that would otherwise require a
              huge investment, thousands of companies are poised for{" "}
              <strong className="text-[#1e1b4b]">incredible growth</strong>.
            </p>
            <p className="text-slate-600">
              The data infrastructure used to belong to enterprise teams with
              seven-figure data budgets. Today, a roofing contractor in Tulsa
              or a windows-and-doors crew in Phoenix can tap the same buyer
              signals — without the contracts, the consultants, or the
              consultative pricing.
            </p>
          </div>
        </section>

        {/* Three pillars */}
        <section className="mx-auto max-w-6xl px-5 py-14 lg:px-8">
          <div className="grid gap-5 md:grid-cols-3">
            {[
              {
                icon: Lightbulb,
                title: "Hard-won perspective",
                body: "Two decades of bootstrapping, building, and selling means we've felt every dollar a small business spends on bad leads. Every product decision starts there.",
              },
              {
                icon: Target,
                title: "Enterprise data, SMB pricing",
                body: "We pool the cost of sourcing, qualifying, and cleaning intent data across our customer base — so you pay a fraction of what enterprise teams pay for the same signal.",
              },
              {
                icon: TrendingUp,
                title: "Built for growth",
                body: "Every feature in BuyerBeacon Pro exists because a small business asked for it. Beacon Score, intent triggers, direct-mail activation — all forged in the trenches.",
              },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`about-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(30,27,75,0.18)]"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#1e1b4b]/[0.06] text-[#1e1b4b]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-[#1e1b4b]">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Stats strip */}
        <section className="border-y border-slate-200 bg-slate-50/60 py-12">
          <div className="mx-auto grid max-w-5xl grid-cols-2 gap-8 px-5 sm:grid-cols-4 lg:px-8">
            {[
              { v: "11", l: "Companies founded" },
              { v: "20 yrs", l: "Founder experience" },
              { v: "280M+", l: "Profiles illuminated" },
              { v: "1000s", l: "Businesses growing" },
            ].map((s) => (
              <div key={s.l} className="text-center">
                <div className="text-3xl font-medium tracking-tight text-[#1e1b4b] sm:text-4xl">
                  {s.v}
                </div>
                <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-4xl px-5 py-20 text-center lg:px-8">
          <h2 className="text-3xl font-medium tracking-tight text-[#1e1b4b] sm:text-4xl">
            See what 11 companies of experience built.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-600">
            Walk through BuyerBeacon Pro with our team. We'll tailor a demo to
            your industry and your activation channels — and you'll leave with
            a plan, not a pitch.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/demo"
              data-testid="about-demo-cta"
              className="group inline-flex items-center gap-2 rounded-xl bg-[#1e1b4b] px-6 py-3 text-sm font-medium text-white transition-all hover:bg-[#1e1b4b]/90 hover:shadow-[0_12px_30px_-10px_rgba(30,27,75,0.5)]"
            >
              Book a demo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/pricing"
              data-testid="about-pricing-cta"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-[#1e1b4b] transition-colors hover:border-[#1e1b4b]/40"
            >
              See pricing
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
